import React, {createRef, ReactNode, RefObject} from "react";
import {Button} from "primereact/button";
import {Menu} from "primereact/menu";
import DomHelper from "../helper/DomHelper";
import Immutable from "../immutable/Immutable";
import {MenuItem} from "primereact/menuitem";
import Translate from "../translation/Translate";
import {
	CertificateEntry,
	CertificateEntryInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Icon from "./Icon";
import {I18nGlobals} from "../translation/i18nInterface";
import FunctionHelper from "../helper/FunctionHelper";
import CodeInput from "./CodeInput";
import PortalPureComponent from "./PortalPureComponent";

/**
 * The expected type of {@link CertificateInput} component
 */
export enum CertificateInputType {
	CERTIFICATE = "certificate",
	PRIVATE_KEY = "private_key"
}

/**
 * The properties for a {@link CertificateInput} component
 */
interface CertificateInputProperties extends I18nGlobals {
	inputId?: string;
	className?: string;
	tooltip?: string;
	value?: string;
	accept?: string;
	serialNumbers?: Array<string>;
	disabled?: boolean;
	type: CertificateInputType;
	certificates?: Immutable<Array<CertificateEntry>>
	onChange?: (value: CertificateEntry) => void;
}

/**
 * The definition for a {@link CertificateInput} component
 */
export class CertificateInputDefinition extends PortalPureComponent<CertificateInputProperties> {
	private menu: RefObject<Menu>;
	private menuId: string;

	/**
	 * Constructs a {@link CerificateInput} component
	 */
	constructor(props: Readonly<CertificateInputProperties>) {
		super(props);

		this.menu = createRef<Menu>();
		this.menuId = DomHelper.generateUUID();

		this.onChange = this.onChange.bind(this);
		this.onDrop = this.onDrop.bind(this);
	}

	/**
	 * Called when the input value changes and calls the given onChange function from props
	 */
	private onChange(data: CertificateEntry): void {
		if (!FunctionHelper.exists(this.props.onChange)) {
			return;
		}

		this.props.onChange!(data);
	}

	/**
	 * Called when a file is dropped on this element
	 */
	private async onDrop(event: React.DragEvent<HTMLDivElement>): Promise<void> {
		let files: FileList = event.dataTransfer.files;
		if (!FunctionHelper.exists(this.props.onChange) || files.length === 0) {
			return;
		}

		let content: string = await files[0].text();
		this.props.onChange!(
			CertificateEntry.fromJson({
				certificate: content
			} as CertificateEntryInterface)
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let menuItems: Array<MenuItem> = [];
		let certificates: Array<CertificateEntry> = FunctionHelper.applyValue(
			this.props.certificates?.get(), []
		);

		for (let certificate of certificates) {
			let entry: CertificateEntry = CertificateEntry.fromJson(certificate);
			let parsedCertificate: x509.X509Certificate;

			try {
				parsedCertificate = new x509.X509Certificate(entry.certificate);
			} catch (e: any) {
				continue;
			}

			if (FunctionHelper.exists(this.props.serialNumbers) &&
				this.props.serialNumbers!.indexOf(parsedCertificate.serialNumber) === -1) {
				continue;
			}

			menuItems.push({
				label: parsedCertificate.subject,
				icon: entry.hasPrivateKey ? "icon icon-key" : "icon empty",
				template: () => {
					return (
						<div className={"p-menuitem-link"} onClick={(event) => {
							this.onChange(entry);
							this.menu.current!.hide(event);
						}}>
							<Icon icon={certificate.keyStoreName === "" ? "icon-globe small" : "icon-user"}
								  tooltip={
									  <Translate {...this.props} data={
										  certificate.keyStoreName === "" ?
											  "inputs.certificate.global" : "inputs.certificate.user"
									  }/>
								  }/>
							<Icon
								icon={FunctionHelper.applyValue(
									certificate.isPrivateKeyReadable, CertificateEntry.getIsPrivateKeyReadableDefault()
								) ? "icon-unlocked" : "icon-locked"}
								tooltip={
									<Translate {...this.props} data={FunctionHelper.applyValue(
										certificate.isPrivateKeyReadable,
										CertificateEntry.getIsPrivateKeyReadableDefault()
									) ? "inputs.certificate.unlocked" : "inputs.certificate.locked"}/>
								}/>
							{certificate.hasPrivateKey &&
								<Icon icon={"icon-key"}
									  tooltip={<Translate {...this.props} data={"inputs.certificate.key"}/>}/>
							}
							{parsedCertificate.subject}
						</div>
					);
				}
			});
		}

		return (
			<CodeInput {...this.props} height={"300px"}
					   options={{
						   contextmenu: false,
						   minimap: {
							   enabled: false
						   }
					   }}
					   onChange={(e) => this.onChange(CertificateEntry.fromJson({
						   certificate: e.value
					   } as CertificateEntryInterface))}
					   buttons={
						   <>
							   {menuItems.length > 0 &&
								   <>
									   <Button icon="icon icon-certificate"
											   tooltip={Translate.value("inputs.certificate.certificate")}
											   onClick={async (event) =>
												   this.menu.current!.toggle(event)
											   }/>
									   <Menu popup={true} ref={this.menu} id={this.menuId}
											 appendTo={document.getElementById("root")}
											 model={menuItems}/>
								   </>
							   }
						   </>
					   }/>
		);
	}
}

/**
 * A CertificateInput component is used to upload a certificate file (i.e. pem files) and parse the contents to a
 * {@link CertificateEntry}
 */
const CertificateInput = React.memo(CertificateInputDefinition,
	(prevProps: Readonly<CertificateInputProperties>, nextProps: Readonly<CertificateInputProperties>) => {
		return ![
			FunctionHelper.exists(nextProps.certificates) && !nextProps.certificates!.equals(prevProps.certificates),
			FunctionHelper.exists(nextProps.disabled) && nextProps.disabled !== prevProps.disabled,
			FunctionHelper.exists(nextProps.value) && nextProps.value !== prevProps.value,
			FunctionHelper.exists(nextProps.accept) && nextProps.accept !== prevProps.accept,
			FunctionHelper.exists(nextProps.type) && nextProps.type !== prevProps.type,
			FunctionHelper.exists(nextProps.className) && nextProps.className !== prevProps.className,
			FunctionHelper.exists(nextProps.tooltip) && nextProps.tooltip !== prevProps.tooltip
		].includes(true);
	}
);

export default CertificateInput;